/**
 * Aba Campanhas: uma linha por campanha com farol (só distância da meta — decisão dela, 09/10/2026),
 * filtros colados na lista (grupo, farol, status, conta) com chips dos ativos, ordenável, 50 por página.
 * Clique na linha abre a aba Anúncios filtrada por aquela campanha.
 *
 * Colunas (pedido dela, 20/10/2026): … Cliques → Vendas → Conv. Ads → Conv. anúncio (pedidos ÷ visitas, para
 * comparar) → CPC → Unidades vendidas → Custo por item vendido. Saíram "Perdido p/ orçamento" e "Em hold".
 * A primeira linha da tabela é o SUBTOTAL do que está filtrado (grupo, farol, status, conta) — soma dos dias,
 * nunca média das médias.
 */
import { memo, useCallback, useMemo, useState } from 'react'
import { Box, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import {
  brl, contarFarol, FILTRO_CAMPANHAS_PADRAO, filtrarCampanhas, grupoDaCampanha, inteiro, nomeConta, pct, resumoPorGrupo, somar, UIDS,
  type CampanhaLinha, type Farol, type FiltroCampanhas, type Somatorio,
} from './logic'
import { FarolChip, Paginacao, Th, Vazio, useOrdenacao } from './ui'

const FAROIS: Farol[] = ['vermelho', 'amarelo', 'verde', 'cinza']
const STATUS = ['active', 'paused', 'deleted']

type LinhaCamp = CampanhaLinha & { meta_origem?: 'campanha' | 'conta' | 'nenhuma' }

const F12 = { fontSize: 12 } as const
const F12B = { fontSize: 12, fontWeight: 600 } as const

function roasTxt(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}×`
}

/** Conversão do anúncio inteiro: "—" com o motivo quando nenhum item da campanha tem visita coletada. */
function ConvAnuncio({ v, semVisita, motivo }: { v: number | null; semVisita: boolean; motivo: string }) {
  if (semVisita) return <Tooltip title={motivo}><span>—</span></Tooltip>
  return <>{pct(v, 2)}</>
}

const Linha = memo(function Linha({ l, onAbrir }: { l: LinhaCamp; onAbrir: (id: number) => void }) {
  const semVisita = l.visitas == null
  const motivo = l.anuncios_na_campanha ? `sem visitas coletadas nos ${l.anuncios_na_campanha} anúncio(s) da campanha` : 'nenhum anúncio da campanha no retrato'
  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onAbrir(l.campaign_id)} title="Abrir os anúncios desta campanha">
      <TableCell><FarolChip farol={l.farol} pequeno /></TableCell>
      <TableCell sx={{ maxWidth: 260 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} noWrap title={l.nome || ''}>{l.nome || `#${l.campaign_id}`}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{nomeConta(l.user_id)} · {l.strategy || '—'} · #{l.campaign_id}</Typography>
      </TableCell>
      <TableCell sx={F12}>{l.status || '—'}</TableCell>
      <TableCell align="right" sx={F12}>{l.roas_target ? `${l.roas_target}×` : '—'}</TableCell>
      <TableCell align="right" sx={F12}>{pct(l.acos_alvo)}{l.meta_origem === 'conta' ? <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary' }}> (conta)</Typography> : null}</TableCell>
      <TableCell align="right" sx={F12B}>{brl(l.cost)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.total_amount)}</TableCell>
      <TableCell align="right" sx={F12B}>{pct(l.acos)}</TableCell>
      <TableCell align="right" sx={F12}>{roasTxt(l.roas)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.clicks)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.itens)}</TableCell>
      <TableCell align="right" sx={F12B}>{pct(l.conversao, 2)}</TableCell>
      <TableCell align="right" sx={F12B}>
        <ConvAnuncio v={l.conversao_anuncio} semVisita={semVisita} motivo={motivo} />
        {!semVisita && l.anuncios_com_visita < l.anuncios_na_campanha ? <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary' }} title={`visitas coletadas em ${l.anuncios_com_visita} de ${l.anuncios_na_campanha} anúncios`}> ({l.anuncios_com_visita}/{l.anuncios_na_campanha})</Typography> : null}
      </TableCell>
      <TableCell align="right" sx={F12}>{brl(l.cpc)}</TableCell>
      <TableCell align="right" sx={F12}>{inteiro(l.unidades)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.custo_unidade)}</TableCell>
      <TableCell align="right" sx={F12}>{brl(l.budget)}</TableCell>
    </TableRow>
  )
})

/** Primeira linha da tabela: soma do que está filtrado. Não é clicável nem entra na ordenação. */
const LinhaSubtotal = memo(function LinhaSubtotal({ s, rotulo }: { s: Somatorio; rotulo: string }) {
  const sx = { fontSize: 12, fontWeight: 700, bgcolor: 'action.hover', borderBottom: '2px solid', borderBottomColor: 'divider' } as const
  return (
    <TableRow>
      <TableCell sx={sx}>Subtotal</TableCell>
      <TableCell sx={sx}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{s.linhas} campanha(s)</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 400 }} noWrap title={rotulo}>{rotulo}</Typography>
      </TableCell>
      <TableCell sx={sx} />
      <TableCell sx={sx} />
      <TableCell sx={sx} />
      <TableCell align="right" sx={sx}>{brl(s.cost)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(s.total_amount)}</TableCell>
      <TableCell align="right" sx={sx}>{pct(s.acos)}</TableCell>
      <TableCell align="right" sx={sx}>{roasTxt(s.roas)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.clicks)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.itens)}</TableCell>
      <TableCell align="right" sx={sx}>{pct(s.conversao, 2)}</TableCell>
      <TableCell align="right" sx={sx}><ConvAnuncio v={s.conversao_anuncio} semVisita={s.visitas == null} motivo="nenhuma campanha filtrada tem visita coletada" /></TableCell>
      <TableCell align="right" sx={sx}>{brl(s.cpc)}</TableCell>
      <TableCell align="right" sx={sx}>{inteiro(s.unidades)}</TableCell>
      <TableCell align="right" sx={sx}>{brl(s.custo_unidade)}</TableCell>
      <TableCell align="right" sx={sx} />
    </TableRow>
  )
})

export function AbaCampanhas({ linhas, onAbrirAnuncios }: { linhas: LinhaCamp[]; onAbrirAnuncios: (campaignId: number) => void }) {
  const [filtro, setFiltro] = useState<FiltroCampanhas>({ ...FILTRO_CAMPANHAS_PADRAO })
  // Grupo pela sigla do nome (ADESC, BDESC, MAR, MAQ, N, H...) — pedido dela, 09/10/2026.
  const [grupo, setGrupo] = useState<string>('')
  // Status e conta recortam a base inteira (grupos e contagem do farol inclusive); o farol só recorta a lista.
  const base = useMemo(() => filtrarCampanhas(linhas, { ...filtro, farol: [] }), [linhas, filtro])
  const grupos = useMemo(() => resumoPorGrupo(base), [base])
  const filtradas = useMemo(() => filtrarCampanhas(grupo ? base.filter((l) => grupoDaCampanha(l.nome) === grupo) : base, { ...filtro, status: [], conta: '' }), [base, filtro, grupo])
  const contagem = useMemo(() => contarFarol(base), [base])
  // Subtotal do que está filtrado (grupo + farol + status + conta) — pedido dela, 20/10/2026.
  const subtotal = useMemo(() => somar(filtradas), [filtradas])
  const ord = useOrdenacao<CampanhaLinha>(filtradas, 'cost')
  const toggleFarol = useCallback((f: Farol) => setFiltro((x) => ({ ...x, farol: x.farol.includes(f) ? x.farol.filter((y) => y !== f) : [...x.farol, f] })), [])
  const abrir = useCallback((id: number) => onAbrirAnuncios(id), [onAbrirAnuncios])
  const ativos = [...(grupo ? [`grupo: ${grupo}`] : []), ...filtro.farol.map((f) => `farol: ${f}`), ...filtro.status.map((s) => `status: ${s}`), ...(filtro.conta ? [`conta: ${nomeConta(filtro.conta)}`] : [])]
  const rotuloSubtotal = ativos.length ? ativos.join(' · ') : 'todas as campanhas da base'

  if (!linhas.length) return <Vazio texto="Nenhuma campanha na base para esta loja e período." />

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Por grupo (sigla do nome da campanha) · clique para filtrar a lista e ver o subtotal do grupo</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={F12}>Grupo</TableCell>
              <TableCell align="right" sx={F12}>Campanhas</TableCell>
              <TableCell align="right" sx={F12}>Gasto</TableCell>
              <TableCell align="right" sx={F12}>Venda atribuída</TableCell>
              <TableCell align="right" sx={F12}>ACOS</TableCell>
              <TableCell align="right" sx={F12}>ROAS</TableCell>
              <TableCell align="right" sx={F12}>Cliques</TableCell>
              <TableCell align="right" sx={F12}>Vendas</TableCell>
              <TableCell align="right" sx={F12}><Tooltip title="Vendas atribuídas ÷ cliques"><span>Conv. Ads</span></Tooltip></TableCell>
              <TableCell align="right" sx={F12}><Tooltip title="Pedidos (base margens) ÷ visitas dos anúncios das campanhas do grupo"><span>Conv. anúncio</span></Tooltip></TableCell>
              <TableCell align="right" sx={F12}>CPC</TableCell>
              <TableCell align="right" sx={F12}>Unidades</TableCell>
              <TableCell align="right" sx={F12}><Tooltip title="Gasto ÷ unidades vendidas"><span>Custo/item</span></Tooltip></TableCell>
              <TableCell sx={F12}>Farol</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grupos.map((g) => (
              <TableRow key={g.grupo} hover selected={grupo === g.grupo} sx={{ cursor: 'pointer' }} onClick={() => setGrupo((x) => (x === g.grupo ? '' : g.grupo))}>
                <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{g.grupo}</TableCell>
                <TableCell align="right" sx={F12}>{g.campanhas}</TableCell>
                <TableCell align="right" sx={F12B}>{brl(g.cost)}</TableCell>
                <TableCell align="right" sx={F12}>{brl(g.total_amount)}</TableCell>
                <TableCell align="right" sx={F12B}>{pct(g.acos)}</TableCell>
                <TableCell align="right" sx={F12}>{roasTxt(g.roas)}</TableCell>
                <TableCell align="right" sx={F12}>{inteiro(g.clicks)}</TableCell>
                <TableCell align="right" sx={F12}>{inteiro(g.itens)}</TableCell>
                <TableCell align="right" sx={F12}>{pct(g.conversao, 2)}</TableCell>
                <TableCell align="right" sx={F12}>{g.visitas == null ? <Tooltip title="sem visitas coletadas nos anúncios do grupo"><span>—</span></Tooltip> : pct(g.conversao_anuncio, 2)}</TableCell>
                <TableCell align="right" sx={F12}>{brl(g.cpc)}</TableCell>
                <TableCell align="right" sx={F12}>{inteiro(g.unidades)}</TableCell>
                <TableCell align="right" sx={F12}>{brl(g.custo_unidade)}</TableCell>
                <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {g.farol.vermelho ? <span style={{ color: '#c62828', fontWeight: 700 }}>{g.farol.vermelho} fora</span> : null}{g.farol.vermelho && (g.farol.amarelo || g.farol.verde) ? ' · ' : ''}
                  {g.farol.amarelo ? <span style={{ color: '#ef6c00', fontWeight: 700 }}>{g.farol.amarelo} até 25%</span> : null}{g.farol.amarelo && g.farol.verde ? ' · ' : ''}
                  {g.farol.verde ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>{g.farol.verde} na meta</span> : null}
                  {g.farol.cinza ? <span style={{ color: '#777' }}> · {g.farol.cinza} sem gasto/venda</span> : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {FAROIS.map((f) => (
          <Box key={f} onClick={() => toggleFarol(f)} sx={{ cursor: 'pointer', opacity: !filtro.farol.length || filtro.farol.includes(f) ? 1 : 0.4 }}>
            <Tooltip title={filtro.farol.includes(f) ? 'tirar do filtro' : 'filtrar por este farol'}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FarolChip farol={f} pequeno />
                <Typography sx={F12}>{contagem[f]}</Typography>
              </Stack>
            </Tooltip>
          </Box>
        ))}
        <TextField select size="small" label="Grupo" value={grupo} onChange={(e) => setGrupo(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">todos</MenuItem>
          {grupos.map((g) => <MenuItem key={g.grupo} value={g.grupo}>{g.grupo} ({g.campanhas})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filtro.status[0] || ''} onChange={(e) => setFiltro((x) => ({ ...x, status: e.target.value ? [e.target.value] : [] }))} sx={{ minWidth: 130 }}>
          <MenuItem value="">todos</MenuItem>
          {STATUS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Conta" value={filtro.conta} onChange={(e) => setFiltro((x) => ({ ...x, conta: e.target.value }))} sx={{ minWidth: 140 }}>
          <MenuItem value="">as duas</MenuItem>
          {UIDS.map((u) => <MenuItem key={u} value={u}>{nomeConta(u)}</MenuItem>)}
        </TextField>
        {ativos.map((a) => <Chip key={a} size="small" label={a} onDelete={() => { setFiltro({ farol: [], status: [], conta: '' }); setGrupo('') }} />)}
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <Th<CampanhaLinha> k="farol" label="Farol" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" hint="Verde: ACOS ≤ alvo · Amarelo: até 25% acima · Vermelho: mais de 25% acima · Cinza: sem gasto, ou gasto ainda sem venda (campanha nova) — não dá para medir contra a meta" />
              <Th<CampanhaLinha> k="nome" label="Campanha" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
              <Th<CampanhaLinha> k="status" label="Status" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} align="left" />
              <Th<CampanhaLinha> k="roas_target" label="ROAS alvo" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Meta oficial do Meli desde jan/2026" />
              <Th<CampanhaLinha> k="acos_alvo" label="ACOS alvo" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="100 ÷ ROAS alvo" />
              <Th<CampanhaLinha> k="cost" label="Gasto" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
              <Th<CampanhaLinha> k="total_amount" label="Venda atribuída" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Direta + indireta (outro produto seu depois do clique), em R$" />
              <Th<CampanhaLinha> k="acos" label="ACOS" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
              <Th<CampanhaLinha> k="roas" label="ROAS" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
              <Th<CampanhaLinha> k="clicks" label="Cliques" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} />
              <Th<CampanhaLinha> k="itens" label="Vendas" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Vendas atribuídas ao Ads (direta + indireta), em quantidade" />
              <Th<CampanhaLinha> k="conversao" label="Conv. Ads" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Vendas ÷ cliques" />
              <Th<CampanhaLinha> k="conversao_anuncio" label="Conv. anúncio" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Anúncio inteiro (Ads + orgânico): pedidos da base margens ÷ visitas da API do ML, dos anúncios desta campanha, no mesmo período. A API do Meli não separa visita orgânica de visita paga." />
              <Th<CampanhaLinha> k="cpc" label="CPC" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Gasto ÷ cliques" />
              <Th<CampanhaLinha> k="unidades" label="Unidades vendidas" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Unidades atribuídas ao Ads (direta + indireta)" />
              <Th<CampanhaLinha> k="custo_unidade" label="Custo por item vendido" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Gasto ÷ unidades vendidas" />
              <Th<CampanhaLinha> k="budget" label="Orçamento/dia" chave={ord.chave} dir={ord.dir} onClick={ord.alternar} hint="Média diária derivada do mensal; não é teto rígido" />
            </TableRow>
          </TableHead>
          <TableBody>
            {ord.ordenadas.length ? <LinhaSubtotal s={subtotal} rotulo={rotuloSubtotal} /> : null}
            {ord.visiveis.map((l) => <Linha key={`${l.user_id}-${l.campaign_id}`} l={l} onAbrir={abrir} />)}
          </TableBody>
        </Table>
      </Box>
      <Paginacao total={ord.ordenadas.length} pagina={ord.pagina} setPagina={ord.setPagina} />
    </Box>
  )
}
